import { apiClient } from '@/lib/api-client';
import { API_CONFIG } from '@/config/api';

/**
 * Address interface from API
 */
export interface CustomerAddress {
  id: number;
  customer_id: number;
  address1: string;
  address2: string | null;
  country: string;
  state: string;
  city: string;
  postcode: string;
  phone: string;
  default_address: number;
  address_type: string;
  company_name: string | null;
  vat_id: string | null;
  first_name: string;
  last_name: string;
  email: string | null;
  prefer_delivery?: string; // e.g., "office", "home"
}

/**
 * Address list response interface
 */
export interface AddressListResponse {
  status: boolean;
  message: string;
  data: CustomerAddress[];
}

/**
 * Create address request interface (matching new API structure)
 */
export interface CreateAddressRequest {
  first_name: string;
  last_name: string;
  company_name?: string;
  address1: string[] | string; // Can be array of strings or single string
  city: string;
  country: string;
  country_name?: string;
  phone: string;
  postcode: string;
  state: string;
  state_code?: string;
  prefer_time?: string; // e.g., "10 am - 6 pm"
  prefer_delivery?: string; // e.g., "office", "home"
  delivery_instructions?: string;
  longitude?: number;
  latitude?: number;
  address_type?: string; // For backward compatibility
  default_address?: number;
}

/**
 * Create address response interface
 */
export interface CreateAddressResponse {
  status: boolean;
  message: string;
  data: CustomerAddress;
}

/**
 * Address service for handling address-related API calls
 */
export class AddressService {
  /**
   * Get customer addresses
   * @param customerId - Customer ID
   * @returns Promise with address list
   */
  static async getCustomerAddresses(customerId: number): Promise<CustomerAddress[]> {
    // Special handling for address endpoint - it's at /api level, not /api/v1
    const response = await apiClient.get<AddressListResponse>(
      `${API_CONFIG.ENDPOINTS.ADDRESS}/${customerId}?token=true`
    );

    if (!response.status) {
      throw new Error(response.message || 'Failed to fetch addresses');
    }

    return response.data;
  }

  /**
   * Create a new address for customer
   * @param addressData - Address data to create
   * @returns Promise with created address
   */
  static async createAddress(
    addressData: CreateAddressRequest
  ): Promise<CustomerAddress> {
    // The new API endpoint is at /api/addresses/create (not v1)
    // Since apiClient prepends BASE_URL, we need to use a relative path
    // that goes back from /api/v1 to /api
    const endpoint = `/../addresses/create?token=true`;
    
    console.log('Creating address with data:', addressData);
    
    const response = await apiClient.post<CreateAddressResponse>(
      endpoint,
      addressData
    );

    console.log('Create address response:', response);

    if (!response.status) {
      throw new Error(response.message || 'Failed to create address');
    }

    return response.data;
  }
}
